/**
 * AM Terminal Phase 3 — reusable action confirmation dialog.
 *
 * Four modes (matching the four backend action routes):
 *   - `unsubscribe`     — required reason textarea (≥10 chars)
 *   - `edit-multiplier` — number input (1-250), no reason
 *   - `pause`           — confirmation only, no reason
 *   - `resume`          — confirmation only, no reason
 *
 * Pause is non-destructive (existing positions exit normally). Edit-multiplier
 * is non-destructive (affects future entries only). Unsubscribe is the only
 * irreversible action — hence the required-reason gate.
 *
 * Reused from both AM Terminal Subscriptions table (mode=unsubscribe / edit-multiplier)
 * and Positions / allocations actions (mode=pause / resume). Single dialog avoids
 * four separate modal components.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';

export type ActionMode = 'unsubscribe' | 'edit-multiplier' | 'pause' | 'resume';

interface ConfirmActionDialogProps {
  open: boolean;
  mode: ActionMode;
  /** Display title for the action subject (e.g. "Premium basket for User X"). */
  subjectLabel: string;
  /** For edit-multiplier mode: current value pre-filled in the input. */
  currentMultiplier?: number;
  onClose: () => void;
  /** unsubscribe → `{reason}`; edit-multiplier → `{lotMultiplier}`; pause/resume → `{}` */
  onConfirm: (payload: { reason?: string; lotMultiplier?: number }) => Promise<void>;
}

const _REASON_MIN_LEN = 10;
const _MULT_MIN = 1;
const _MULT_MAX = 250;

const TITLES: Record<ActionMode, string> = {
  'unsubscribe': 'Unsubscribe Managed User',
  'edit-multiplier': 'Edit Lot Multiplier',
  'pause': 'Pause Allocation',
  'resume': 'Resume Allocation',
};

const CONFIRM_LABELS: Record<ActionMode, string> = {
  'unsubscribe': 'Unsubscribe',
  'edit-multiplier': 'Update',
  'pause': 'Pause',
  'resume': 'Resume',
};

const SUBTITLES: Record<ActionMode, string> = {
  'unsubscribe':
    'This cancels the subscription + deletes the allocation. Open positions will not be auto-exited. Required reason for compliance audit.',
  'edit-multiplier':
    'Affects FUTURE entries only — positions already opened keep their original size. Re-validated against listing min/max + per-leg cap.',
  'pause':
    'Stops NEW entries on this allocation. Existing positions exit normally on schedule. Reversible via Resume.',
  'resume':
    'Re-activates the allocation. Only allocations paused via this terminal can be resumed here.',
};

const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  mode,
  subjectLabel,
  currentMultiplier,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [multiplierInput, setMultiplierInput] = useState<string>(
    String(currentMultiplier ?? 1),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setMultiplierInput(String(currentMultiplier ?? 1));
    setError(null);
  }, [open, mode, currentMultiplier]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (mode === 'unsubscribe') {
      const trimmed = reason.trim();
      if (trimmed.length < _REASON_MIN_LEN) {
        setError(`Reason must be at least ${_REASON_MIN_LEN} characters`);
        return;
      }
      setSubmitting(true);
      try {
        await onConfirm({ reason: trimmed });
      } catch (err: any) {
        setError(err?.message || 'Action failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mode === 'edit-multiplier') {
      const parsed = Number(multiplierInput);
      if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
        setError('Lot multiplier must be a whole number');
        return;
      }
      if (parsed < _MULT_MIN || parsed > _MULT_MAX) {
        setError(`Lot multiplier must be between ${_MULT_MIN} and ${_MULT_MAX}`);
        return;
      }
      if (currentMultiplier !== undefined && parsed === currentMultiplier) {
        setError('Selection matches the current value — nothing to update');
        return;
      }
      setSubmitting(true);
      try {
        await onConfirm({ lotMultiplier: parsed });
      } catch (err: any) {
        setError(err?.message || 'Action failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // pause / resume — no payload
    setSubmitting(true);
    try {
      await onConfirm({});
    } catch (err: any) {
      setError(err?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={handleClose} maxWidth="md" ariaLabel={TITLES[mode]}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {TITLES[mode]}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {subjectLabel}
            </p>
          </div>
          <button onClick={handleClose} disabled={submitting}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {SUBTITLES[mode]}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        {mode === 'unsubscribe' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Reason * (min {_REASON_MIN_LEN} chars)
            </label>
            <textarea
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(null); }}
              disabled={submitting}
              rows={3}
              placeholder="e.g. operator override — risk limit breach on instrument"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Stored on the audit row for compliance review.
            </p>
          </div>
        )}

        {mode === 'edit-multiplier' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              New Lot Multiplier *
            </label>
            <Input
              type="number"
              inputMode="numeric"
              min={_MULT_MIN}
              max={_MULT_MAX}
              step={1}
              value={multiplierInput}
              onChange={(e) => { setMultiplierInput(e.target.value); setError(null); }}
              disabled={submitting}
            />
            {currentMultiplier !== undefined && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Current: <span className="font-medium">{currentMultiplier}</span>
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={submitting}
            variant={mode === 'unsubscribe' ? 'danger' : 'primary'}
          >
            {CONFIRM_LABELS[mode]}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmActionDialog;
